import { Fragment, useEffect, useRef, useState } from "react"
import { Send } from "lucide-react"
import { LifestyleSummary } from "@/components/activation/LifestyleSummary"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

const EMPTY_MESSAGES = []
const EMPTY_PROMPTS = []
const EMPTY_SUGGESTIONS = []
const STEP_TWO_LOCK_FOLLOW_UP = "If you're happy with this, click Lock Strategy below."
const OPERATING_STYLE_FOLLOW_UP = "That gives me your operating style. Now I can recommend strategy routes from that involvement level. Would you like me to do that?"
const OPERATING_PREFERENCE_FIELD = "operatingPreference"
const STRATEGY_SUGGESTION_FIELDS = new Set(["primaryStrategy", "secondaryStrategy"])
const STEP_TWO_LOCK_FOLLOW_UP_PATTERN = /If\s+you['’]re\s+happy with this,\s*click Lock Strategy below\.\s*/i

const SUGGESTION_STATUS_LABELS = {
  applied: "Applied field update",
  dismissed: "Dismissed field update",
  pending: "Suggested field update",
}

const DEFERRED_FOLLOW_UP_PATTERNS = [
  new RegExp(`^(?<body>[\\s\\S]*?)(?<followUp>${STEP_TWO_LOCK_FOLLOW_UP_PATTERN.source})$`, "i"),
  /^(?<body>[\s\S]*?)(?<followUp>[^.?!\n]*\?\s*)$/,
]

function splitContentForSuggestions(content, hasSuggestions) {
  const message = content.toString().trim()

  const match = DEFERRED_FOLLOW_UP_PATTERNS
    .map((pattern) => message.match(pattern))
    .find((candidate) => candidate?.groups)

  if (!hasSuggestions) {
    return match?.groups?.followUp.trim().toLowerCase() === STEP_TWO_LOCK_FOLLOW_UP.toLowerCase()
      ? { body: match.groups.body.trim(), followUp: match.groups.followUp.trim() }
      : { body: message, followUp: null }
  }

  if (!match?.groups) return { body: message, followUp: null }

  return {
    body: match.groups.body.trim(),
    followUp: match.groups.followUp.trim(),
  }
}

function suggestionStatus(suggestion) {
  return suggestion.status || "pending"
}

function isPendingSuggestion(suggestion) {
  return suggestionStatus(suggestion) === "pending"
}

function isAppliedSuggestion(suggestion) {
  return suggestionStatus(suggestion) === "applied"
}

function isResolvedSuggestion(suggestion) {
  return !isPendingSuggestion(suggestion)
}

function isOperatingPreferenceSuggestion(suggestion) {
  return suggestion.field === OPERATING_PREFERENCE_FIELD
}

function isStrategySuggestion(suggestion) {
  return STRATEGY_SUGGESTION_FIELDS.has(suggestion.field)
}

function isStepTwoLockFollowUp(followUp) {
  return followUp?.replace("’", "'").toLowerCase() === STEP_TWO_LOCK_FOLLOW_UP.toLowerCase()
}

function fallbackFollowUpForSuggestions(suggestions, followUp) {
  const hasStrategySuggestion = suggestions.some(isStrategySuggestion)
  const hasAppliedStrategySuggestion = suggestions.some((suggestion) => isStrategySuggestion(suggestion) && isAppliedSuggestion(suggestion))
  if (followUp && isStepTwoLockFollowUp(followUp)) return hasAppliedStrategySuggestion ? followUp : null
  if (followUp) return followUp
  if (hasStrategySuggestion && hasAppliedStrategySuggestion) {
    return STEP_TWO_LOCK_FOLLOW_UP
  }
  if (suggestions.some((suggestion) => isOperatingPreferenceSuggestion(suggestion) && isResolvedSuggestion(suggestion))) {
    return OPERATING_STYLE_FOLLOW_UP
  }

  return null
}

export function AskRobPanel({
  subtitle,
  prompts = EMPTY_PROMPTS,
  messages = EMPTY_MESSAGES,
  isLoading = false,
  error,
  onAsk,
  onApplySuggestion,
  onDismissSuggestion,
  suggestionMode = "all",
  placeholder = "Ask a question…",
}) {
  const [draft, setDraft] = useState("")
  const messagesPaneRef = useRef(null)
  const inputRef = useRef(null)
  const shouldRefocusInputRef = useRef(false)
  const showPrompts = messages.length === 0 && prompts.length > 0

  useEffect(() => {
    if (!messages.length) return

    const messagesPane = messagesPaneRef.current
    if (!messagesPane) return

    messagesPane.scrollTop = messagesPane.scrollHeight
  }, [messages])

  useEffect(() => {
    if (isLoading || !shouldRefocusInputRef.current) return

    inputRef.current?.focus()
    shouldRefocusInputRef.current = false
  })

  const submit = () => {
    const message = draft.trim()
    if (!message || isLoading) return
    setDraft("")
    shouldRefocusInputRef.current = true
    onAsk?.(message)
  }

  const refocusInput = () => {
    shouldRefocusInputRef.current = true
  }

  const handleApplySuggestion = (messageIndex, suggestionId) => {
    onApplySuggestion?.(messageIndex, suggestionId)
    refocusInput()
  }

  const handleDismissSuggestion = (messageIndex, suggestionId) => {
    onDismissSuggestion?.(messageIndex, suggestionId)
    refocusInput()
  }

  const handleKeyDown = (event) => {
    if (event.key !== "Enter") return
    event.preventDefault()
    submit()
  }

  return (
    <div className="sticky top-20 space-y-6 rounded-xl border border-border bg-card p-6 shadow-sm">
      <div>
        <div className="mb-2 flex items-center gap-2">
          <img
            src="/rob_headshot.jpeg"
            alt="Rob"
            className="size-5 rounded-full object-cover"
          />
          <h3 className="text-sm font-semibold text-foreground">Ask Rob</h3>
        </div>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>

      {showPrompts ? (
        <div className="space-y-2">
          {prompts.map((prompt) => (
            <button
              key={prompt}
              type="button"
              disabled={isLoading}
              onClick={() => onAsk?.(prompt)}
              className="group w-full rounded-lg border border-border bg-background p-3 text-left transition-colors hover:border-primary/50 hover:bg-primary/5"
            >
              <p className="text-xs text-foreground transition-colors group-hover:text-primary">
                {prompt}
              </p>
            </button>
          ))}
        </div>
      ) : null}

      <div className="space-y-4">
        <div ref={messagesPaneRef} className="max-h-[300px] min-h-[120px] overflow-y-auto rounded-lg border border-border bg-muted/30 p-4">
          {messages.length ? (
            <div className="space-y-3">
              {messages.map((message, index) => {
                const suggestions = message.fieldSuggestions || EMPTY_SUGGESTIONS
                const splitMessage = splitContentForSuggestions(message.content, suggestions.length > 0)
                const followUp = fallbackFollowUpForSuggestions(suggestions, splitMessage.followUp)
                const hasPendingSuggestions = suggestions.some(isPendingSuggestion)
                const visibleSuggestions = suggestionMode === "sequential"
                  ? suggestions.filter(isPendingSuggestion).slice(0, 1)
                  : suggestions

                return (
                  <Fragment key={`${message.role}-${index}`}>
                    <Message
                      messageRole={message.role}
                      content={splitMessage.body}
                      lifestyleSummary={message.lifestyleSummary}
                      messageIndex={index}
                      suggestions={visibleSuggestions}
                      onApplySuggestion={handleApplySuggestion}
                      onDismissSuggestion={handleDismissSuggestion}
                    />
                    {followUp && !hasPendingSuggestions ? (
                      <Message
                        messageRole="assistant"
                        content={followUp}
                        messageIndex={index}
                        suggestions={EMPTY_SUGGESTIONS}
                        onApplySuggestion={handleApplySuggestion}
                        onDismissSuggestion={handleDismissSuggestion}
                      />
                    ) : null}
                  </Fragment>
                )
              })}
            </div>
          ) : (
            <p className="text-center text-xs italic text-muted-foreground">
              Your conversation will appear here
            </p>
          )}
        </div>
        {error ? <p className="text-xs text-destructive">{error}</p> : null}
        <div className="flex items-center gap-2">
          <Input
            type="text"
            aria-label="Ask a question"
            ref={inputRef}
            placeholder={placeholder}
            value={draft}
            disabled={isLoading}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1"
          />
          <Button
            type="button"
            aria-label="Send message"
            size="icon-sm"
            disabled={isLoading || !draft.trim()}
            onClick={submit}
          >
            <Send className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

function Message({
  messageRole,
  content,
  lifestyleSummary,
  messageIndex,
  suggestions,
  onApplySuggestion,
  onDismissSuggestion,
}) {
  return (
    <div
      className={cn(
        "rounded-lg px-3 py-2 text-xs",
        messageRole === "user"
          ? "ml-6 bg-primary text-primary-foreground"
          : "mr-6 border border-border bg-background text-foreground",
      )}
    >
      {content ? <p>{content}</p> : null}
      {lifestyleSummary ? <LifestyleSummary summary={lifestyleSummary} /> : null}
      <MessageSuggestions
        messageIndex={messageIndex}
        suggestions={suggestions}
        onApplySuggestion={onApplySuggestion}
        onDismissSuggestion={onDismissSuggestion}
      />
    </div>
  )
}

function MessageSuggestions({ messageIndex, suggestions, onApplySuggestion, onDismissSuggestion }) {
  if (!suggestions.length) return null

  return (
    <div className="mt-3 space-y-2">
      {suggestions.map((suggestion) => (
        <FieldSuggestion
          key={suggestion.id}
          messageIndex={messageIndex}
          suggestion={suggestion}
          onApplySuggestion={onApplySuggestion}
          onDismissSuggestion={onDismissSuggestion}
        />
      ))}
    </div>
  )
}

function FieldSuggestion({ messageIndex, suggestion, onApplySuggestion, onDismissSuggestion }) {
  const status = suggestionStatus(suggestion)
  const isPending = status === "pending"

  return (
    <div
      className={cn(
        "min-w-0 rounded-lg border border-border bg-card p-3",
        status === "applied" && "bg-muted/50",
        status === "dismissed" && "opacity-70",
      )}
    >
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        {SUGGESTION_STATUS_LABELS[status] || SUGGESTION_STATUS_LABELS.pending}
      </p>
      <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-foreground">{suggestion.label}</p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Current value: {suggestion.currentValue || "blank"}
          </p>
        </div>
        <p className="min-w-0 max-w-full wrap-break-word font-bold text-foreground sm:max-w-[55%] sm:text-right">
          {suggestion.displayValue || suggestion.suggestedValue}
        </p>
      </div>
      {isPending ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            type="button"
            size="xs"
            aria-label={`Apply ${suggestion.label}`}
            onClick={() => onApplySuggestion?.(messageIndex, suggestion.id)}
          >
            Apply
          </Button>
          <Button
            type="button"
            variant="outline"
            size="xs"
            aria-label={`Dismiss ${suggestion.label}`}
            onClick={() => onDismissSuggestion?.(messageIndex, suggestion.id)}
          >
            Dismiss
          </Button>
        </div>
      ) : (
        <p className="mt-2 text-[11px] font-semibold text-muted-foreground">
          {status === "applied" ? "Applied to form" : "Dismissed"}
        </p>
      )}
    </div>
  )
}
